import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { BarChart3, CheckCircle2, ArrowRight, Shield, Zap, Globe, Plus, Minus } from "lucide-react";
import SEO from "../components/SEO";

export default function Accounting() {
    const [openFaqIndex, setOpenFaqIndex] = useState(null);

    const toggleFaq = (index) => {
        setOpenFaqIndex(openFaqIndex === index ? null : index);
    };

    const faqItems = [
        {
            question: "What is the best AI accounting software for small businesses?",
            answer: "blinqbooks is a top-rated AI-powered accounting software designed specifically for small businesses and startups. It automates day-to-day bookkeeping, generates GST-compliant invoices, scans receipts using AI, and performs instant bank reconciliation without manual entry."
        },
        {
            question: "How does AI automate accounting and bookkeeping?",
            answer: "Our AI accounting engine uses machine learning to read receipt details (via AI Scan), categorize your expenses, link transactions automatically to your bank feed, and generate real-time profit and loss (P&L) statements, saving you hours of manual spreadsheet work every week."
        },
        {
            question: "Can blinqbooks handle Sales Tax, VAT, and GST invoices?",
            answer: "Yes, blinqbooks is built for multi-jurisdiction tax compliance. You can create customized, professional invoices compliant with US State Sales Tax, UK HMRC MTD, EU VAT OSS, UAE FTA VAT, and Canadian/Indian GST in seconds with automated tax calculations."
        },
        {
            question: "Is my financial data secure in the cloud?",
            answer: "Absolutely. Security is our top priority. blinqbooks uses enterprise-grade 256-bit SSL encryption to secure your financial data, with redundant cloud backups and secure Supabase-backed authentication to ensure your books are always safe and audit-ready."
        }
    ];

    // Structured SEO Schemas
    const schemas = [
        {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "blinqbooks Accounting",
            "operatingSystem": "All",
            "applicationCategory": "BusinessApplication, FinancialApplication",
            "description": "AI-powered cloud accounting software for invoicing, automated bookkeeping, and real-time financial reporting.",
            "offers": {
                "@type": "Offer",
                "price": "0.00",
                "priceCurrency": "USD",
                "description": "14-day free trial, no credit card required"
            }
        },
        {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqItems.map((faq) => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                    "@type": "Answer",
                    "text": faq.answer
                }
            }))
        }
    ];

    return (
        <div className="min-h-screen bg-base text-primary">
            <SEO
                title="Best AI Accounting Software for Small Business | Cloud Invoicing"
                description="blinqbooks is the best AI cloud accounting software in 2026. Automate GST invoicing, bookkeeping, reporting, bank reconciliation & expense tracking. Start free today."
                schema={schemas}
            />

            {/* Hero Section */}
            <section className="py-20 px-4 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                    <motion.div
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/10 text-blue-600 dark:text-blue-400 text-xs font-bold uppercase mb-6">
                            <BarChart3 className="w-4 h-4" />
                            Smart Accounting
                        </div>
                        <h1 className="text-4xl md:text-6xl font-bold mb-6 text-primary dark:text-white leading-tight">
                            Your Finances, <br />
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-500 to-cyan-500">Automated by AI</span>
                        </h1>
                        <p className="text-lg text-secondary dark:text-gray-300 mb-8 max-w-lg leading-relaxed">
                            Stop wasting hours on manual bookkeeping. blinqbooks handles your multi-currency invoicing, Sales Tax / VAT compliance, expense tracking, and bank reconciliation automatically.
                        </p>
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/signup" className="btn-primary w-full sm:w-auto px-8 py-4 shadow-lg text-lg text-center block">
                                Start Free Trial
                            </Link>
                            <Link to="/contact" className="btn-secondary w-full sm:w-auto px-8 py-4 shadow-sm text-lg text-center block">
                                Watch Demo
                            </Link>
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8 }}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-blue-500/10 blur-[100px] rounded-full"></div>
                        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-xl rounded-2xl p-6 overflow-hidden">
                            <div className="flex items-center justify-between mb-8 pb-4">
                                <span className="font-bold text-lg dark:text-white">Live Cash Flow</span>
                                <span className="text-green-500 font-bold">+24% vs last month</span>
                            </div>
                            <div className="flex items-end gap-3 h-48">
                                {[40, 70, 45, 90, 65, 85, 55, 95].map((h, i) => (
                                    <motion.div
                                        key={i}
                                        initial={{ height: 0 }}
                                        animate={{ height: `${h}%` }}
                                        transition={{ duration: 1, delay: i * 0.1 }}
                                        className="flex-1 bg-gradient-to-t from-blue-600 to-cyan-400 rounded-t-lg"
                                    ></motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Feature Bento Grid */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-bold mb-4 text-primary dark:text-white">Everything automated</h2>
                        <p className="text-secondary dark:text-gray-300">Six core pillars of our AI accounting engine.</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { title: "Global Tax-Ready Invoices", desc: "Generate professional Sales Tax, VAT & GST invoices in seconds.", icon: <Zap className="text-yellow-500" /> },
                            { title: "Auto Reconciliation", desc: "Sync your bank feeds and let AI match transactions.", icon: <Shield className="text-blue-500" /> },
                            { title: "Insightful Reports", desc: "P&L, Balance Sheet, and custom reports at one click.", icon: <BarChart3 className="text-purple-500" /> },
                            { title: "Multi-Currency", desc: "Transact globally with real-time exchange rates.", icon: <Globe className="text-green-500" /> },
                            { title: "Inventory Sync", desc: "Your stock levels update automatically with sales.", icon: <CheckCircle2 className="text-emerald-500" /> },
                            { title: "Audit Ready", desc: "Keep all your data organized and compliant.", icon: <Shield className="text-red-500" /> },
                        ].map((f, i) => (
                            <motion.div
                                key={i}
                                whileHover={{ y: -5 }}
                                className="p-8 rounded-2xl bg-slate-500/[0.02] dark:bg-white/[0.01] hover:bg-blue-500/[0.06] transition-all duration-300"
                            >
                                <div className="p-3 rounded-xl bg-blue-500/10 dark:bg-blue-500/20 w-fit mb-4">
                                    {f.icon}
                                </div>
                                <h3 className="text-xl font-bold mb-2 text-primary dark:text-white">{f.title}</h3>
                                <p className="text-secondary dark:text-gray-400 text-sm leading-relaxed">{f.desc}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ Accordion Section */}
            <section className="py-20 px-4 bg-slate-500/[0.01] border-y border-slate-200/50 dark:border-slate-800/50">
                <div className="max-w-4xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-5xl font-black mb-4 text-primary dark:text-white">Frequently Asked Questions</h2>
                        <p className="text-secondary dark:text-gray-400 font-medium">Answers to common questions about our AI-driven accounting software.</p>
                    </div>

                    <div className="space-y-4">
                        {faqItems.map((faq, index) => {
                            const isOpen = openFaqIndex === index;
                            return (
                                <div 
                                    key={index} 
                                    className="border border-slate-200/80 dark:border-slate-800 rounded-2xl overflow-hidden bg-white dark:bg-slate-900/90 transition-all duration-300 shadow-sm"
                                >
                                    <button 
                                        onClick={() => toggleFaq(index)}
                                        className="w-full flex items-center justify-between p-6 text-left font-bold text-slate-900 dark:text-white hover:text-accent dark:hover:text-accent transition-colors focus:outline-none"
                                    >
                                        <span className="text-base md:text-lg text-slate-900 dark:text-white font-semibold pr-4">{faq.question}</span>
                                        <span className="p-2 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 shrink-0">
                                            {isOpen ? <Minus className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                                        </span>
                                    </button>

                                    <AnimatePresence initial={false}>
                                        {isOpen && (
                                            <motion.div
                                                initial={{ height: 0, opacity: 0 }}
                                                animate={{ height: "auto", opacity: 1 }}
                                                exit={{ height: 0, opacity: 0 }}
                                                transition={{ duration: 0.25, ease: "easeInOut" }}
                                            >
                                                <div className="px-6 pb-6 pt-2 border-t border-slate-100 dark:border-slate-800/80 text-slate-600 dark:text-slate-300 text-sm md:text-base leading-relaxed">
                                                    {faq.answer}
                                                </div>
                                            </motion.div>
                                        )}
                                    </AnimatePresence>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="py-20 px-4">
                <div className="max-w-4xl mx-auto bg-gradient-to-tr from-blue-600 to-indigo-600 dark:from-slate-800 dark:to-slate-900 rounded-3xl p-12 text-center text-white relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-white/20 rounded-full blur-[80px] -mr-32 -mt-32"></div>
                    <div className="absolute bottom-0 left-0 w-64 h-64 bg-blue-400/20 rounded-full blur-[80px] -ml-32 -mb-32"></div>
                    <h2 className="text-3xl md:text-5xl font-bold mb-6 relative z-10 text-white">Scale your business faster</h2>
                    <p className="text-blue-100 mb-8 text-lg relative z-10 font-medium">Join 50,000+ businesses running on blinqbooks AI.</p>
                    <Link to="/signup" className="px-10 py-4 bg-white text-blue-600 dark:text-white dark:bg-blue-600 rounded-full font-bold hover:scale-105 hover:shadow-lg transition-all flex items-center gap-2 mx-auto relative z-10 w-fit">
                        Get Started for Free <ArrowRight className="w-5 h-5" />
                    </Link>
                </div>
            </section>
        </div>
    );
}
